import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StartOverlay } from '@/components/deai-startoverlay';
import { DEAI3DScene } from '@/components/deai-3dscene';
import { DEAIChatBox, AIMessageDisplay, UserMessageDisplay } from '@/components/deai-chatbox';
import { DEAIApp } from '@/components/deai-app';
import { useAppStore } from '@/hooks/use-app-store';
import { useAIPotionUIBridge, type GameEndData } from '@/hooks/tgs2025/potionmaker/use-ai-potion-ui-bridge';
import { PERSONAS } from '@/types/persona';


//tgs2025 potion game videos
import { VideoOverlay } from '@/components/tgs2025/potionmaker/video-overlay';
import { PotionVideoDemo } from '@/components/tgs2025/potionmaker/demo/potion-video-demo';
import { Cauldron } from '@/components/tgs2025/potionmaker/cauldron-overlay';
import { VRMExpressionDemo } from '@/components/tgs2025/potionmaker/demo/vrm-expression-demo';
import { useVideoTriggerHandler } from '@/hooks/tgs2025/potionmaker/use-video-trigger-handler';
import { PotionUI } from '@/components/tgs2025/potionmaker/PotionUI';
import { AttemptCounter } from '@/components/tgs2025/potionmaker/attempt-counter';
import { GameEndOverlay, type GameEndState } from '@/components/tgs2025/potionmaker/GameEndOverlay';

export const PotionCraftPage = () => {
    const { triggerAvatarAnimator } = useAppStore();
    const navigate = useNavigate();
    const [gameEndState, setGameEndState] = useState<GameEndState>({
      isVisible: false, // Hidden until real game ends
      gameWon: false, 
      potionAttempts: []
    });
    
    // Game end callback to handle win/lose scenarios
    const handleGameEnd = (data: GameEndData) => {
      console.log('🏁 Game ended:', data);
      setGameEndState({
        isVisible: true,
        gameWon: data.gameWon,
        potionAttempts: data.potionAttempts || []
      });
    };
    
    // Initialize potion game integration with AI
    const AIPotionUIBridge = useAIPotionUIBridge(handleGameEnd);
    
    // Initialize video trigger handling
    useVideoTriggerHandler();
    
    const handleHomeNavigation = () => {
      navigate('/');
    };
    
    const handleGameEndClose = () => {
      setGameEndState({
        isVisible: false,
        gameWon: false,
        potionAttempts: []
      });
    };

    useEffect(()=>{
        triggerAvatarAnimator(
            0,
            'StartIdle'
        );
    },[triggerAvatarAnimator])
  
  const phonemeMultipliers = new Map<string, number>();
  phonemeMultipliers.set("aa", 1);
  phonemeMultipliers.set("ih", 2);
  phonemeMultipliers.set("ou", 2);
  phonemeMultipliers.set("ee", 2);
  phonemeMultipliers.set("oh", 2);

  return (
    <div className="h-screen">
        <div 
          className="absolute inset-0 bg-cover bg-fixed z-0"
          style={{ 
            backgroundImage: 'url(/img/potion_room.png)',
            backgroundPosition: '60% center'
          }}
        />
        <StartOverlay soundURL='audio/click.wav' />
        
        {/* AI Message Display - Fixed at top center, above PotionUI */}
        <AIMessageDisplay />
        
        {/* User Message Display - Fixed at bottom center */}
        <UserMessageDisplay />

        <div className="w-full h-full">
            <DEAI3DScene 
              className="absolute inset-0 h-full w-full"
              placeholderImgSrc="img/loading-placeholder.png"
              vrmUrl={"avatar/Witch.vrm"}
              vrmaUrl={"animations/Noura/Idle/QATAR Idle_clean_02_upd.vrma"}
              vrmaLibRoot={"animations/Noura/"}
              vrmaLibConfig={"AnimationConfig.json"}
              animatorLibRoot={"animators/Noura/"}
              animatorLibConfig={"AnimatorConfig.json"}
              cameraPosition={{ x: -0.03, y: 1.85, z: -2.5}}
              cameraRotationEuler={{x: 0.2, y: Math.PI, z: 0}}
              phonemeMultipliers={phonemeMultipliers}
            />
            
            {/* Cauldron overlay positioned in front of DEAI3DScene */}
            {!gameEndState.isVisible && <Cauldron />}
            
            {/* Chat Input - Positioned at bottom right */}
            <DEAIChatBox className="absolute bottom-[80px] left-1/2 -translate-x-1/2 w-3/4 max-w-lg text-white rounded-lg z-50" />
        </div>
        
        {/* Video overlay for potion effects - positioned above everything */}
        <VideoOverlay className="z-50" />
        
        {/* Game End Overlay - positioned above UI but below video effects */}
        <GameEndOverlay 
          gameEndState={gameEndState}
          onHome={handleHomeNavigation}
          onClose={handleGameEndClose}
        />

       {/*
        <PotionVideoDemo />
        
 
        <VRMExpressionDemo />
        *

        {/* Potion UI - full screen */}
        <div className="absolute inset-0 w-full h-full z-10">
          <PotionUI 
            width="100%"
            height="100%"
            onElementClick={(elementId, element) => {
              console.log('Clicked:', elementId, element);
            }}
            onElementHover={(elementId, element) => {
              if (element) {
                console.log('Hovering:', elementId);
              }
            }}
            onControlsReady={(controls) => {
              // Controls are now managed by the potionGameIntegration
              //console.log('PotionUI controls ready and integrated with AI system');
            }}
            externalControls={{
              activeButton: AIPotionUIBridge.activeButton,
              buttonTexts: AIPotionUIBridge.buttonTexts,
              handleButtonToggle: AIPotionUIBridge.handleButtonToggle,
              controls: AIPotionUIBridge.controls
            }}
          />
        </div>    
        
      <div className="absolute top-[15dvh] right-[2dvh] flex flex-col items-end gap-2">
        {/* 
          <div className="flex flex-col gap-2 mb-4">
            <button 
              onClick={() => {
                console.log('🏆 Triggering Win Scenario Test');
                setGameEndState({
                  isVisible: true,
                  gameWon: true,
                  potionAttempts: [
                    { attemptNumber: 1, ingredients: ['Roses', 'Honey', 'Spider Silk'], result: { shape: 'Normal', color: 'Green', nameStyle: 'weird', potionName: 'Weird Green Concoction', isLovePotion: false }},
                    { attemptNumber: 2, ingredients: ['Lavender', 'Vanilla', 'Moon Water'], result: { shape: 'Heart', color: 'Blue', nameStyle: 'mystic', potionName: 'Mystic Heart Elixir', isLovePotion: false }},
                    { attemptNumber: 3, ingredients: ['Rose Petals', 'Honey', 'Moonstone'], result: { shape: 'Heart', color: 'Pink', nameStyle: 'romantic', potionName: 'True Love Potion', isLovePotion: true }}
                  ]
                });
              }}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-bold shadow-lg"
            >
              🏆 Test Win
            </button>
            <button 
              onClick={() => {
                console.log('💀 Triggering Lose Scenario Test');
                setGameEndState({
                  isVisible: true,
                  gameWon: false,
                  potionAttempts: [
                    { attemptNumber: 1, ingredients: ['Bat Wings', 'Charcoal', 'Spider Silk'], result: { shape: 'Weird', color: 'Black', nameStyle: 'dark', potionName: 'Nightmare Brew', isLovePotion: false }},
                    { attemptNumber: 2, ingredients: ['Lemon', 'Ginger', 'Sunflower'], result: { shape: 'Normal', color: 'Yellow', nameStyle: 'cheerful', potionName: 'Sunshine Tonic', isLovePotion: false }},
                    { attemptNumber: 3, ingredients: ['Salt', 'Pepper', 'Water'], result: { shape: 'Normal', color: 'Green', nameStyle: 'mundane', potionName: 'Bland Mixture', isLovePotion: false }}
                  ]
                });
              }}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-bold shadow-lg"
            >
              💀 Test Lose
            </button>
            <button 
              onClick={() => setGameEndState(prev => ({...prev, isVisible: !prev.isVisible}))}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-bold shadow-lg"
            >
              👁️ Toggle Overlay
            </button>
          </div>
          */}
          <AttemptCounter />
      </div>
      
      <DEAIApp persona={PERSONAS.POTION_MAKER} />
    </div>
  );
};